import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import "./Navbar.css";
import ThemeToggle from "./ThemeToggle";
import SocialLinks from "./SocialLinks";

const Navbar = ({ onOpenAuthModal }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [appDropdownOpen, setAppDropdownOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const closeMenu = () => {
    setIsMenuOpen(false);
    setAppDropdownOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header className={`jmi-navbar-wrapper ${isScrolled ? "scrolled" : ""}`}>
      <div className="jmi-container navbar-inner">
        {/* Brand Logo */}
        <Link to="/" className="jmi-brand" onClick={closeMenu}>
          <svg 
            viewBox="65 146 370 165" 
            className="brand-logo-img" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <g fill="none" stroke="#B52B2E" strokeWidth="6" strokeLinecap="butt">
              <path d="M98,156 V247 A15,15 0 0 1 83,262 H74"/>
              <path d="M110,156 V247 A27,27 0 0 1 83,274 H74"/>
              <path d="M122,156 V247 A39,39 0 0 1 83,286 H74"/>
              <path d="M134,156 V247 A51,51 0 0 1 83,298 H74"/>
            </g>
            <g fill="#B52B2E">
              <polygon points="187,296 187,156 193.3,156 247.15,259 240.85,259 193,167.48 193,296"/>
              <polygon points="329,296 329,156 322.7,156 268.85,259 275.15,259 323,167.48 323,296"/>
              <polygon points="201,156 207.3,156 261.15,259 254.85,259"/>
              <polygon points="215,156 221.3,156 275.15,259 268.85,259"/>
              <polygon points="308.7,156 315,156 271.15,239.88 268,233.85"/>
              <polygon points="294.7,156 301,156 264.15,226.49 261,220.46"/>
              <polygon points="199,296 199,188.52 205,199.99 205,296"/>
              <polygon points="317,296 317,188.52 311,199.99 311,296"/>
              <polygon points="211,296 211,211.47 217,222.95 217,296"/>
              <polygon points="305,296 305,211.47 299,222.95 299,296"/>
              <polygon points="223,296 223,234.42 229,245.9 229,296"/>
              <polygon points="293,296 293,234.42 287,245.9 287,296"/>
              <rect x="380" y="156" width="6" height="140"/>
              <rect x="392" y="156" width="6" height="140"/>
              <rect x="404" y="156" width="6" height="140"/>
              <rect x="416" y="156" width="6" height="140"/>
            </g>
          </svg>
          <div className="brand-text-block">
            <span className="brand-tagline red-gradient-text">JEWEL MART INDIA</span>
          </div>
        </Link>

        {/* Desktop Navigation Links */}
        <nav className="desktop-nav-links">
          <Link to="/" className={`nav-item ${isActive("/") ? "active" : ""}`}>
            Home
          </Link>
          <Link
            to="/buyer"
            className={`nav-item ${isActive("/buyer") ? "active" : ""}`}
          >
            Buyer
          </Link>
          <Link
            to="/seller"
            className={`nav-item ${isActive("/seller") ? "active" : ""}`}
          >
            Seller
          </Link>
          <Link
            to="/operator"
            className={`nav-item ${isActive("/operator") ? "active" : ""}`}
          >
            Operator
          </Link>
          <Link
            to="/how-it-works"
            className={`nav-item ${isActive("/how-it-works") ? "active" : ""}`}
          >
            How It Works
          </Link>
          <Link
            to="/categories"
            className={`nav-item ${isActive("/categories") ? "active" : ""}`}
          >
            Categories
          </Link>
          <Link
            to="/blog"
            className={`nav-item ${location.pathname.startsWith("/blog") ? "active" : ""}`}
          >
            Blog
          </Link>
          <Link
            to="/about"
            className={`nav-item ${isActive("/about") ? "active" : ""}`}
          >
            About
          </Link>
          <Link
            to="/faq"
            className={`nav-item ${isActive("/faq") ? "active" : ""}`}
          >
            FAQ
          </Link>
        </nav>

        {/* Desktop Actions - Hidden on Mobile */}
        <div className="navbar-actions desktop-only">
          <ThemeToggle compact />
          {/* App Download Dropdown */}
          <div className="app-dropdown-container">
            <button
              className="btn btn-secondary btn-sm app-drop-btn"
              onClick={() => setAppDropdownOpen(!appDropdownOpen)}
              aria-label="Download Apps"
            >
              <span>Get Apps</span>
              <span className="drop-caret">{appDropdownOpen ? "▴" : "▾"}</span>
            </button>

            {appDropdownOpen && (
              <div className="app-dropdown-menu">
                <div className="dropdown-header">MOBILE APPS</div>
                <Link to="/buyer" className="dropdown-item" onClick={closeMenu}>
                  <div className="item-title">JMI Buyer</div>
                  <div className="item-sub">Wholesale sourcing & orders</div>
                </Link>
                <Link
                  to="/seller"
                  className="dropdown-item"
                  onClick={closeMenu}
                >
                  <div className="item-title">JMI Seller</div>
                  <div className="item-sub">
                    Catalogue & dispatch management
                  </div>
                </Link>
              </div>
            )}
          </div>

          {/* Direct Apply for Access Button */}
          <Link to="/apply" className="btn btn-primary btn-sm">
            Apply  →
          </Link>
        </div>

        {/* Mobile hamburger toggle - Always visible on mobile */}
        <button
          className="mobile-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle Navigation"
        >
          {isMenuOpen ? "✕" : "☰"}
        </button>
      </div>

      {/* Mobile Drawer */}
      {isMenuOpen && (
        <div className="mobile-drawer">
          <div className="mobile-links">
            <Link to="/" className="mobile-link" onClick={closeMenu}>
              Home Platform
            </Link>
            <Link
              to="/apply"
              className="mobile-link highlight-buyer"
              onClick={closeMenu}
            >
              <span>Apply for Platform Access</span>
              <span className="badge-pill">Verification Req</span>
            </Link>
            <Link to="/buyer" className="mobile-link" onClick={closeMenu}>
              <span>For Buyers</span>
              <span className="badge-pill">Source Wholesale</span>
            </Link>
            <Link to="/seller" className="mobile-link" onClick={closeMenu}>
              <span>For Sellers</span>
              <span className="badge-pill gold">Expand Sales</span>
            </Link>
            <Link to="/operator" className="mobile-link" onClick={closeMenu}>
              <span>For Operators</span>
              <span
                className="badge-pill"
                style={{
                  background: "rgba(201, 168, 76, 0.15)",
                  color: "var(--tech-accent)",
                  borderColor: "rgba(201, 168, 76, 0.3)",
                }}
              >
                Regional Hub
              </span>
            </Link>
            <Link
              to="/blog"
              className="mobile-link highlight-seller"
              onClick={closeMenu}
            >
              <span>Industry Journal & Rates</span>
              <span className="badge-pill">Bullion Reports</span>
            </Link>
            <Link
              to="/how-it-works"
              className="mobile-link"
              onClick={closeMenu}
            >
              How JMI Works
            </Link>
            <Link to="/categories" className="mobile-link" onClick={closeMenu}>
              Jewellery Categories
            </Link>
            <Link to="/about" className="mobile-link" onClick={closeMenu}>
              About JMI
            </Link>
            <Link to="/faq" className="mobile-link" onClick={closeMenu}>
              FAQs & Support
            </Link>
            <Link to="/contact" className="mobile-link" onClick={closeMenu}>
              Contact Platform
            </Link>
          </div>

          <SocialLinks variant="drawer" showWhatsappLine={false} />

          <div className="mobile-footer-actions">
            <ThemeToggle />
            <div className="mobile-action-grid">
              <Link to="/apply" className="btn btn-primary" onClick={closeMenu}>
                Apply for Access
              </Link>
              <Link
                to="/blog"
                className="btn btn-outline-gold"
                onClick={closeMenu}
              >
                Read Blog
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
